import React from 'react';
import { ShoppingCart, Trash2 } from 'lucide-react';
import { AGE_GROUPS, ANANDAMELA, DAYS, MealType, Quote, eur } from 'types';

/** Booking-page-only cart view of the current selection, grouped by day + meal type so each plate the guest picked
 * is one removable row. Review/checkout still use the plain CostSummary - this is deliberately not shared with it. */
export default function ReservationCart({ quote, onRemoveMeal, onRemoveAnandamela, children }: {
  quote: Quote;
  onRemoveMeal: (date: string, type: MealType) => void;
  onRemoveAnandamela: () => void;
  children?: React.ReactNode;
}) {
  const empty = quote.lines.length === 0 && quote.anandamela.length === 0;
  const mealGroups = DAYS.flatMap((day) =>
    (['veg', 'nonveg'] as MealType[]).map((type) => {
      const lines = quote.lines.filter((l) => l.date === day.date && l.mealType === type);
      return lines.length ? { day, type, lines } : null;
    })
  ).filter((g): g is { day: typeof DAYS[number]; type: MealType; lines: typeof quote.lines } => g !== null);

  return (
    <section className="bg-white rounded-2xl shadow-md border border-rose-100 p-5" aria-label="Your reservation">
      <div className="flex items-center gap-2 mb-1">
        <ShoppingCart className="w-5 h-5 text-rose-700" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-rose-900">Your Reservation</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">Updates automatically as you choose meals - your selected plates and headcount appear here in real time.</p>

      {empty && <p className="text-sm text-gray-500">Nothing selected yet. Choose meals or Anandamela attendees to see your reservation.</p>}

      <div className="space-y-3">
        {mealGroups.map(({ day, type, lines }) => {
          const total = lines.reduce((s, l) => s + l.amountCents, 0);
          const label = `${day.name} ${type === 'veg' ? 'Veg' : 'Non-Veg'}`;
          return (
            <div key={day.date + type} className="rounded-lg bg-rose-50/60 border border-rose-100 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-rose-900">{label}</span>
                <button type="button" aria-label={`Remove ${label} from your reservation`} onClick={() => onRemoveMeal(day.date, type)}
                  className="text-rose-400 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>
              </div>
              {lines.map((l) => (
                <div key={l.ageGroup} className="flex justify-between text-xs text-gray-600">
                  <span>{AGE_GROUPS.find((a) => a.id === l.ageGroup)!.name} ({l.quantity})</span>
                  <span>{l.amountCents ? eur(l.amountCents) : 'Free'}</span>
                </div>
              ))}
              <div className="flex justify-between text-xs font-semibold text-rose-900 mt-1 pt-1 border-t border-rose-100">
                <span>Subtotal</span><span>{eur(total)}</span>
              </div>
            </div>
          );
        })}

        {quote.anandamela.length > 0 && (
          <div className="rounded-lg bg-amber-50/60 border border-amber-100 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-amber-900">{ANANDAMELA.name}</span>
              <button type="button" aria-label="Remove Anandamela registration from your reservation" onClick={onRemoveAnandamela}
                className="text-amber-400 hover:text-amber-700"><Trash2 className="w-4 h-4" /></button>
            </div>
            {quote.anandamela.map((l) => (
              <div key={l.ageGroup} className="flex justify-between text-xs text-gray-600">
                <span>{AGE_GROUPS.find((a) => a.id === l.ageGroup)!.name} ({l.quantity})</span>
                <span>{l.amountCents ? eur(l.amountCents) : 'Free'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {quote.donationCents > 0 && (
        <div className="flex justify-between text-sm text-gray-600 mt-3"><span>Donation</span><span>{eur(quote.donationCents)}</span></div>
      )}

      <div className="border-t border-rose-100 mt-4 pt-3 flex justify-between text-lg font-bold text-rose-900" data-testid="total">
        <span>Total</span><span>{eur(quote.totalCents)}</span>
      </div>

      {children}
    </section>
  );
}
